import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiClient } from "../lib/api";
import { CheckCircle, WarningCircle, ArrowRight } from "@phosphor-icons/react";

const STREAM_LABELS = {
  prekickoff: "pre-kickoff reminders",
  matchweek_results: "matchweek result digests",
  rank_digest: "weekly rank movement digests",
};

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const stream = params.get("stream") || "";

  const [state, setState] = useState({ status: "loading", data: null, error: null });

  useEffect(() => {
    if (!token) {
      setState({ status: "error", error: "This unsubscribe link is missing its token.", data: null });
      return;
    }
    apiClient
      .post("/unsubscribe", { token, stream })
      .then(({ data }) => setState({ status: "done", data, error: null }))
      .catch((err) =>
        setState({
          status: "error",
          error: err?.response?.data?.detail || "This link is invalid or expired.",
          data: null,
        })
      );
  }, [token, stream]);

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-16 grid-bg">
      <div
        data-testid="unsubscribe-card"
        className="w-full max-w-lg surface border border-white/10 p-10 text-center"
      >
        {state.status === "loading" && (
          <>
            <div className="text-[10px] uppercase tracking-[0.3em] text-primary-yellow font-bold">
              Working on it
            </div>
            <h1 className="font-heading text-3xl uppercase font-black mt-4">
              Updating your preferences…
            </h1>
          </>
        )}

        {state.status === "done" && (
          <>
            <CheckCircle size={56} weight="fill" className="text-primary-yellow mx-auto" />
            <div className="mt-6 text-[10px] uppercase tracking-[0.3em] text-primary-yellow font-bold">
              Done
            </div>
            <h1 className="font-heading text-3xl uppercase font-black mt-3" data-testid="unsubscribe-success-title">
              You&apos;re unsubscribed
            </h1>
            <p className="text-sm text-white/60 mt-4 leading-relaxed">
              {state.data.disabled_streams.length === 1 ? (
                <>
                  We won&apos;t send you <strong className="text-white">{STREAM_LABELS[state.data.disabled_streams[0]] || "these emails"}</strong> any more.
                </>
              ) : (
                <>All PitchIQ email streams have been turned off for your account.</>
              )}
            </p>
            <p className="text-xs text-white/40 mt-6">
              Signed in as <span className="text-white/70">{state.data.email}</span>
            </p>
            <div className="mt-8 flex flex-col gap-3">
              <Link
                to="/profile"
                data-testid="unsubscribe-manage-prefs-btn"
                className="inline-flex items-center justify-center gap-2 bg-primary-yellow text-black uppercase tracking-[0.2em] font-bold py-3 px-6 text-xs hover:-translate-y-0.5 transition-transform"
              >
                Manage all preferences <ArrowRight size={12} weight="bold" />
              </Link>
              <Link
                to="/"
                className="text-xs text-white/40 uppercase tracking-widest hover:text-white/60"
              >
                Back to PitchIQ
              </Link>
            </div>
          </>
        )}

        {state.status === "error" && (
          <>
            <WarningCircle size={56} weight="fill" className="text-red-400/80 mx-auto" />
            <div className="mt-6 text-[10px] uppercase tracking-[0.3em] text-red-400/80 font-bold">
              Couldn&apos;t unsubscribe
            </div>
            <h1 className="font-heading text-3xl uppercase font-black mt-3" data-testid="unsubscribe-error-title">
              Link expired or invalid
            </h1>
            <p className="text-sm text-white/60 mt-4 leading-relaxed">{state.error}</p>
            <p className="text-xs text-white/40 mt-6">
              You can still disable email streams by signing in and going to your profile.
            </p>
            <div className="mt-8">
              <Link
                to="/login"
                data-testid="unsubscribe-login-btn"
                className="inline-flex items-center justify-center gap-2 bg-primary-yellow text-black uppercase tracking-[0.2em] font-bold py-3 px-6 text-xs hover:-translate-y-0.5 transition-transform"
              >
                Sign in to manage <ArrowRight size={12} weight="bold" />
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
